import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import { ArrowUpRight } from 'lucide-react';

import { FadeIn, PageTransition, Reveal } from '../components/PageTransition';
import Section from '../components/Section';
import PostRow from '../components/PostRow';
import UssdPanel from '../components/UssdPanel';
import AskSection from '../components/AskSection';
import { useSiteChat } from '../contexts/ChatContext';
import Seo from '../components/Seo';
import work from '../data/work';
import { requestPosts } from '../actions/post';
import { sortPostsByDate } from '../utils/postHelpers';

import avatarImage from '../assets/avator_1.jpeg';
import {
  REACT_APP_OWNER,
  REACT_APP_SITE_URL,
  REACT_APP_SOCIAL_FACEBOOK,
  REACT_APP_SOCIAL_GITHUB,
  REACT_APP_SOCIAL_LINKEDIN,
  REACT_APP_SOCIAL_TWITTER,
} from '../env';

const SOCIALS = [
  { label: 'GitHub', href: REACT_APP_SOCIAL_GITHUB },
  { label: 'LinkedIn', href: REACT_APP_SOCIAL_LINKEDIN },
  { label: 'Twitter', href: REACT_APP_SOCIAL_TWITTER },
  { label: 'Facebook', href: REACT_APP_SOCIAL_FACEBOOK },
].filter((item) => Boolean(item.href));

function Home({ posts, fetchPosts }) {
  const { openChat } = useSiteChat();

  useEffect(() => {
    if (!posts || posts.length === 0) fetchPosts();
  }, [fetchPosts, posts]);

  const siteUrl = REACT_APP_SITE_URL || 'https://dennisnyabuti.com';
  const latest = sortPostsByDate(posts || []).slice(0, 4);

  const profileSchema = {
    '@context': 'https://schema.org',
    '@type': 'Person',
    name: REACT_APP_OWNER || 'Dennis M. Nyabuti',
    url: siteUrl,
    image: `${siteUrl}${avatarImage}`,
    jobTitle: 'Senior Software Engineer',
    homeLocation: { '@type': 'Place', name: 'Kigali, Rwanda' },
    sameAs: SOCIALS.map((item) => item.href),
  };

  return (
    <>
      <Seo
        title="Dennis M. Nyabuti | Senior Software Engineer"
        description="Senior Software Engineer in Kigali building USSD and offline-first software for the places connectivity forgot. Writing on engineering, cloud architecture and AI."
        path="/"
        image={avatarImage}
        keywords="Dennis Nyabuti, software engineer, USSD, offline-first, Kigali, Rwanda, React, Node.js, AWS, AI integration"
        schema={profileSchema}
      />

      <PageTransition>
        {/* Hero — the thesis, answered by a live USSD session */}
        <div className="shell grid items-center gap-12 pb-20 pt-12 sm:pt-20 lg:grid-cols-[minmax(0,1.25fr)_minmax(19rem,0.75fr)] lg:gap-16 lg:pb-28">
          <div>
            <FadeIn delay={0.05}>
              <p className="label">Senior Software Engineer &middot; Kigali, Rwanda</p>
            </FadeIn>

            <FadeIn delay={0.15}>
              <h1 className="mt-6 text-display">
                Software that works
                <br />
                where the signal
                <br />
                <em className="italic text-signal">doesn&rsquo;t.</em>
              </h1>
            </FadeIn>

            <FadeIn delay={0.3}>
              <p className="mt-8 max-w-xl text-deck text-muted-foreground">
                For ten years I have built for the people the internet reaches last — smallholder
                farmers on feature phones, field agents past the end of the road. Senior engineer at
                One Acre Fund, co-founder at BuniAI.
              </p>
            </FadeIn>

            <FadeIn delay={0.4}>
              <div className="mt-10 flex items-center gap-4 border-t border-rule pt-6">
                <img
                  alt="Dennis Nyabuti"
                  src={avatarImage}
                  width={56}
                  height={56}
                  className="h-14 w-14 rounded-sm border border-rule object-cover"
                />
                <div className="text-sm">
                  <p className="font-medium">Dennis M. Nyabuti</p>
                  <button
                    type="button"
                    onClick={openChat}
                    className="label mt-1 flex items-center gap-1.5 transition-colors hover:text-signal"
                  >
                    <span className="relative flex h-1.5 w-1.5" aria-hidden="true">
                      <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-signal opacity-60" />
                      <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-signal" />
                    </span>
                    Ask my assistant
                  </button>
                </div>
              </div>
            </FadeIn>
          </div>

          <FadeIn delay={0.55}>
            <UssdPanel />
          </FadeIn>
        </div>

        <div className="shell space-y-24 pb-8">
          {/* 1 — Writing */}
          <Reveal>
            <Section
              number="1"
              title="Writing"
              aside={latest.length > 0 ? <Link to="/posts" className="link-rule">All posts</Link> : null}
            >
              {latest.length > 0 ? (
                <div>
                  {latest.map((post) => (
                    <PostRow key={post.ID || post.id} post={post} />
                  ))}
                </div>
              ) : (
                <p className="border-b border-rule py-10 text-muted-foreground">
                  Nothing published yet. The first post is being written.
                </p>
              )}
            </Section>
          </Reveal>

          {/* 2 — Work */}
          <Reveal>
            <Section id="work" number="2" title="Work" aside="Selected">
              <div>
                {work.map((item) => {
                  const Wrapper = item.href ? 'a' : 'div';
                  const wrapperProps = item.href
                    ? { href: item.href, target: '_blank', rel: 'noopener noreferrer' }
                    : {};

                  return (
                    <Wrapper
                      key={item.title}
                      {...wrapperProps}
                      className="group grid gap-2 border-b border-rule py-7 grid-cols-1 sm:grid-cols-[8.5rem_minmax(0,1fr)] sm:gap-8"
                    >
                      <div className="label pt-1.5 sm:pt-2.5">
                        {item.period}
                        <span className="mt-1 block normal-case tracking-normal">{item.role}</span>
                      </div>

                      <div className="space-y-3">
                        <h3 className="flex items-start gap-2 text-headline transition-colors group-hover:text-signal">
                          <span>{item.title}</span>
                          {item.href && (
                            <ArrowUpRight
                              className="mt-1.5 h-4 w-4 shrink-0 opacity-0 -translate-x-1 transition-all duration-300 group-hover:opacity-100 group-hover:translate-x-0"
                              aria-hidden="true"
                            />
                          )}
                        </h3>
                        <p className="max-w-prose [overflow-wrap:anywhere] text-[0.9375rem] leading-relaxed text-muted-foreground">
                          {item.summary}
                        </p>
                        <p className="label">{item.stack.join(' / ')}</p>
                      </div>
                    </Wrapper>
                  );
                })}
              </div>
            </Section>
          </Reveal>

          {/* 0 — the assistant, closing the menu the hero opened */}
          <Reveal>
            <AskSection />
          </Reveal>
        </div>
      </PageTransition>
    </>
  );
}

Home.propTypes = {
  posts: PropTypes.array,
  fetchPosts: PropTypes.func.isRequired,
};

export default connect(
  (state) => ({ posts: state.posts }),
  (dispatch) => ({ fetchPosts: () => dispatch(requestPosts()) }),
)(Home);
